// Maps an assistant tool's filter input (FilterSchema.cs keys) to a Request List URL
// (useUrlSearchState.ts param names). Several keys are renamed between the two contracts.

const SCALAR_PASSTHROUGH = ["from", "to", "q"] as const;

// Assistant array-filter key -> Request List URL param. `status` is handled specially below.
const ARRAY_KEY_MAP: Record<string, string> = {
  client: "client",
  requestType: "requestType",
  lineOfBusiness: "lob",
  engagementType: "eng",
  assignedTo: "assigned",
  workerCountry: "country",
  slownessBucket: "bucket",
};

// The grid's visible status filter (`calculatedStatus`) only models these two values.
const CALCULATED_STATUSES = new Set(["Completed", "InProgress"]);

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.map((v) => String(v)).filter((v) => v.length > 0);
}

/**
 * Builds a `/requests?…` path from an assistant tool's filter `input` (already-parsed JSON), or
 * `null` if nothing maps cleanly. Arrays serialize as repeated keys, matching `useUrlSearchState`.
 *
 * When the input carries a non-empty `groupBy` (a get_request_aggregate call) the link recreates
 * the **Aggregate** tab — group-by + min-sample + the exact ranking (aggSort/aggDir) — so the user
 * can verify the assistant's ranked list on screen. Otherwise it lands on the **Instances** tab.
 */
export function assistantFiltersToRequestUrl(input: unknown): string | null {
  if (input === null || typeof input !== "object") return null;
  const obj = input as Record<string, unknown>;
  const params = new URLSearchParams();

  for (const [assistantKey, urlKey] of Object.entries(ARRAY_KEY_MAP)) {
    for (const v of asStringArray(obj[assistantKey])) params.append(urlKey, v);
  }

  // status -> calculatedStatus, but only the values the grid filter understands.
  const statuses = asStringArray(obj.status).filter((s) => CALCULATED_STATUSES.has(s));
  for (const s of statuses) params.append("calculatedStatus", s);

  for (const key of SCALAR_PASSTHROUGH) {
    const v = obj[key];
    if (typeof v === "string" && v.length > 0) params.set(key, v);
  }

  // Onboarding/Redeployment buckets apply to both tabs.
  for (const flag of ["onboardingsOnly", "redeployment"] as const) {
    if (obj[flag] === true) params.set(flag, "true");
  }

  // Aggregate query → recreate the Aggregate tab (group-by + min-sample + ranking).
  const groupBy = asStringArray(obj.groupBy);
  if (groupBy.length > 0) {
    params.set("tab", "aggregate");
    for (const g of groupBy) params.append("groupBy", g);
    if (typeof obj.minSampleSize === "number" && obj.minSampleSize >= 1) {
      params.set("minSample", String(Math.floor(obj.minSampleSize)));
    }
    if (typeof obj.sortBy === "string" && obj.sortBy.length > 0) params.set("aggSort", obj.sortBy);
    if (obj.sortDir === "asc" || obj.sortDir === "desc") params.set("aggDir", obj.sortDir);
    return `/requests?${params.toString()}`;
  }

  // List/summary queries: only produce a link when something actually maps; pin the Instances tab.
  if ([...params.keys()].length === 0) return null;
  params.set("tab", "instances");
  return `/requests?${params.toString()}`;
}
